import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { Check, ChevronDown, Layers, Plus } from 'lucide-react'
import { useMemo } from 'react'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { getRegionLabel } from 'v2/shared/utils/region-label'
import { useOrgSwitch } from './create-organization-modal/use-org-switch'
import { OrgFlag } from './org-flag'

/** Organization switcher in the top header, used with the workspace sidebar. */
export const HeaderOrgSwitcher = () => {
  const { data: profileData } = useProfileQuery()
  const { organizations, isAdmin, switchTo, openCreateOrg, createOrgModal } = useOrgSwitch()
  const currentOrgUuid = profileData?.organizations?.[0]?.org_id

  const sorted = useMemo(
    () => (organizations ?? []).slice().sort((a, b) => a.org_name.localeCompare(b.org_name)),
    [organizations],
  )
  const current = organizations?.find((org) => org.org_uuid === currentOrgUuid)

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 min-w-0 max-w-48 gap-2 px-2 font-inter text-s-500 normal-case tracking-normal md:max-w-40 xl:max-w-48"
            aria-label={
              current
                ? `Switch organization, current organization: ${current.org_name}, ${getRegionLabel(current.region)} region`
                : 'Switch organization'
            }
            title={
              current ? `${current.org_name} · ${getRegionLabel(current.region)} region` : undefined
            }
            data-testid="organization-menu-trigger"
          >
            {current ? (
              <>
                <OrgFlag region={current.region} />
                <span className="truncate">{current.org_name}</span>
              </>
            ) : (
              <span className="inline-block h-4 w-24 animate-pulse rounded bg-muted" />
            )}
            <ChevronDown className="size-3.5 shrink-0 text-secondary" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="w-80 max-w-[calc(100vw-2rem)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-lg bg-card p-2"
          aria-label="Organizations"
        >
          <DropdownMenuLabel className="flex items-center gap-2 text-xs-500 text-secondary">
            <Layers className="size-4" aria-hidden />
            <span className="flex-1">Switch organization</span>
            <span className="text-xs-400">{sorted.length}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {sorted.map((org) => {
            const isCurrent = org.org_uuid === currentOrgUuid
            return (
              <DropdownMenuItem
                key={org.org_uuid}
                className="gap-3 rounded-md py-3 pl-3 pr-2"
                onSelect={() => {
                  if (!isCurrent) switchTo(org)
                }}
                data-testid={`organization-menuitem-${org.org_uuid}`}
              >
                <OrgFlag region={org.region} />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="break-words text-s-500">{org.org_name}</p>
                  <p className="text-xs-400 text-secondary">{getRegionLabel(org.region)} region</p>
                </div>
                <span className="size-4 shrink-0" aria-hidden>
                  {isCurrent && <Check className="size-4 text-brand-text" />}
                </span>
              </DropdownMenuItem>
            )
          })}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-s-400"
                onSelect={openCreateOrg}
                data-testid="create-organization-button"
              >
                <Plus className="size-4" strokeWidth={1.5} />
                Create New Organization
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {createOrgModal}
    </>
  )
}
